// @adr SPARK-0055-document-displays-use-one-public-boundary@3: a live PDF generator with its own isolated renderer, approved by @adr SPARK-0062-studio-notebooks-export-as-pdf@1.
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { downloadBlob } from "@/lib/file-download";
import { useAppName } from "@/lib/use-app-name";
import { loadNotebookFigures } from "@/lib/studio-notebook-figures";
import { notebookPdfFileName } from "@/lib/studio-notebook-file-names";
import {
  buildStudioNotebookPdfPayload,
  readStudioNotebookPdf,
  type StudioNotebookPdfVariant,
} from "@/lib/studio-notebook-pdf-model";
import {
  isStudioNotebookRendererResponse,
  studioNotebookRendererDocumentUrl,
  type StudioNotebookRendererRequest,
} from "@/lib/studio-notebook-renderer-protocol";

/** One notebook to save as a PDF: the Markdown the export writes for the chosen option. */
export interface StudioNotebookPdfJob {
  name: string;
  markdown: string;
  variant: StudioNotebookPdfVariant;
}

// Long enough for a notebook with figures on a slow connection; a renderer that never answers must not keep
// the export busy for good.
export const STUDIO_NOTEBOOK_PDF_TIMEOUT_MS = 60_000;

// Mounted for one job: it loads the hidden renderer, sends the notebook once the renderer is ready and its
// figures have loaded, and saves the file the renderer hands back. What it sent and whether it finished are kept
// for that one job, so the page mounts it anew for every job, keyed by the job.
export function StudioNotebookPdfExport({
  job,
  onDone,
  onError,
}: {
  job: StudioNotebookPdfJob;
  onDone: () => void;
  onError: () => void;
}) {
  const intl = useIntl();
  const brand = useAppName();
  const frame = useRef<HTMLIFrameElement | null>(null);
  const [ready, setReady] = useState(false);
  const sent = useRef(false);
  const finished = useRef(false);
  const callbacks = useRef({ onDone, onError });
  useEffect(() => {
    callbacks.current = { onDone, onError };
  });
  const finish = useCallback((outcome: "onDone" | "onError") => {
    if (finished.current) return;
    finished.current = true;
    callbacks.current[outcome]();
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      if (event.origin !== window.location.origin) return;
      if (event.source !== frame.current?.contentWindow) return;
      if (!isStudioNotebookRendererResponse(event.data)) return;
      if (event.data.type === "studio-notebook-renderer-ready") {
        setReady(true);
      } else if (event.data.type === "studio-notebook-pdf") {
        downloadBlob(event.data.blob, notebookPdfFileName(job.name, job.variant));
        finish("onDone");
      } else {
        finish("onError");
      }
    }
    window.addEventListener("message", onMessage);
    const timer = setTimeout(() => finish("onError"), STUDIO_NOTEBOOK_PDF_TIMEOUT_MS);
    return () => {
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
    };
  }, [job, finish]);

  useEffect(() => {
    if (!ready || sent.current) return;
    sent.current = true;
    // A notebook that cannot be read or sent fails at once, not after the timeout (spark#1252). It is read once:
    // the figures it names load, and the same reading becomes the payload.
    void Promise.resolve()
      .then(() => readStudioNotebookPdf(job))
      .then(async (draft) => {
        const figures = await loadNotebookFigures(draft.figureKeys);
        const message: StudioNotebookRendererRequest = {
          type: "studio-notebook-render",
          payload: buildStudioNotebookPdfPayload({ draft, figures, brand, locale: intl.locale, now: new Date() }),
        };
        frame.current?.contentWindow?.postMessage(message, window.location.origin);
      })
      .catch(() => finish("onError"));
  }, [ready, job, brand, intl.locale, finish]);

  return (
    <iframe
      ref={frame}
      src={studioNotebookRendererDocumentUrl()}
      title={intl.formatMessage({ id: "studio.pdf.rendererTitle", defaultMessage: "PDF export of the notebook" })}
      aria-hidden="true"
      tabIndex={-1}
      className="hidden"
    />
  );
}
